import React, { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { format, addMonths, subMonths, setMonth, setYear } from 'date-fns';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface SearchDatePickerProps {
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  onClose: () => void;
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const SearchDatePicker: React.FC<SearchDatePickerProps> = ({
  value,
  onChange,
  onClose
}) => {
  const [displayMonth, setDisplayMonth] = useState(value || new Date());
  
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);

  const handleMonthChange = (monthIndex: string) => {
    setDisplayMonth(setMonth(displayMonth, parseInt(monthIndex)));
  };

  const handleYearChange = (year: string) => {
    setDisplayMonth(setYear(displayMonth, parseInt(year)));
  };

  const handleSelect = (date: Date | undefined) => {
    onChange(date);
    if (date) {
      onClose();
    }
  };

  const handleQuickSelect = (monthsAhead: number) => {
    const date = addMonths(new Date(), monthsAhead);
    setDisplayMonth(date);
  };

  return (
    <div className="p-4">
      {/* Quick Month Buttons */}
      <div className="flex gap-2 mb-4 pb-4 border-b border-border overflow-x-auto">
        <button
          onClick={() => handleQuickSelect(0)}
          className="px-4 py-2 text-sm font-medium rounded-full border border-border hover:bg-muted transition-colors whitespace-nowrap"
        >
          This Month
        </button>
        <button
          onClick={() => handleQuickSelect(1)}
          className="px-4 py-2 text-sm font-medium rounded-full border border-border hover:bg-muted transition-colors whitespace-nowrap"
        >
          Next Month
        </button>
        <button
          onClick={() => handleQuickSelect(2)}
          className="px-4 py-2 text-sm font-medium rounded-full border border-border hover:bg-muted transition-colors whitespace-nowrap"
        >
          In 2 Months
        </button>
        <button
          onClick={() => handleQuickSelect(3)}
          className="px-4 py-2 text-sm font-medium rounded-full border border-border hover:bg-muted transition-colors whitespace-nowrap"
        >
          In 3 Months
        </button>
      </div>

      {/* Month/Year Selector */}
      <div className="flex items-center justify-center gap-2 mb-4">
        <button
          onClick={() => setDisplayMonth(subMonths(displayMonth, 1))}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        
        <Select value={displayMonth.getMonth().toString()} onValueChange={handleMonthChange}>
          <SelectTrigger className="w-[130px] border-0 bg-transparent font-medium text-center">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-background border border-border z-[2200]">
            {MONTHS.map((month, index) => (
              <SelectItem key={month} value={index.toString()}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={displayMonth.getFullYear().toString()} onValueChange={handleYearChange}>
          <SelectTrigger className="w-[90px] border-0 bg-transparent font-medium text-center">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-background border border-border z-[2200]">
            {years.map((year) => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <button
          onClick={() => setDisplayMonth(addMonths(displayMonth, 1))}
          className="p-2 hover:bg-muted rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Two-Month Calendar */}
      <div className="flex gap-6 justify-center">
        <DayPicker
          mode="single"
          selected={value}
          onSelect={handleSelect}
          month={displayMonth}
          onMonthChange={setDisplayMonth}
          numberOfMonths={2}
          showOutsideDays
          className="p-0 pointer-events-auto"
          classNames={{
            months: "flex gap-6",
            month: "space-y-4",
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "text-sm font-medium",
            nav: "hidden",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-muted-foreground w-10 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "h-10 w-10 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-none [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-none last:[&:has([aria-selected])]:rounded-none focus-within:relative focus-within:z-20",
            day: cn(buttonVariants({ variant: "ghost" }), "h-10 w-10 p-0 font-normal aria-selected:opacity-100 rounded-full hover:rounded-full"),
            day_range_end: "day-range-end",
            day_selected: "bg-primary !text-white hover:bg-primary hover:!text-white focus:bg-primary focus:!text-white rounded-full",
            day_today: "border border-primary text-primary aria-selected:!bg-primary aria-selected:!text-white bg-transparent rounded-full",
            day_outside: "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
            day_disabled: "text-muted-foreground opacity-50",
            day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
            day_hidden: "invisible",
          }}
        />
      </div>

      {/* Clear Button */}
      {value && (
        <div className="flex justify-center mt-4 pt-4 border-t border-border">
          <button
            onClick={() => onChange(undefined)}
            className="text-sm text-muted-foreground hover:text-foreground underline underline-offset-4"
          >
            Clear date
          </button>
        </div>
      )}
    </div>
  );
};
